import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw, Loader, Book, ArrowRightToLine, ArrowLeftToLine } from 'lucide-react';
import '../css/magazine-viewer.css';

const MagazineViewer = ({ pdfUrl }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [zoom, setZoom] = useState(1);
    const [pdf, setPdf] = useState(null);
    const [pageImages, setPageImages] = useState([]);
    const [flipping, setFlipping] = useState(false);
    const [direction, setDirection] = useState(null);
    const [isMobileView, setIsMobileView] = useState(false);
    const containerRef = useRef(null);

    // Check for mobile view on mount and when window resizes
    useEffect(() => {
        const checkMobileView = () => {
            setIsMobileView(window.innerWidth < 768);
        };

        // Initial check
        checkMobileView();

        // Add resize listener
        window.addEventListener('resize', checkMobileView);

        // Cleanup
        return () => window.removeEventListener('resize', checkMobileView);
    }, []);

    // Initialize PDF.js and load the document
    useEffect(() => {
        const loadPdf = async () => {
            if (!pdfUrl) return;

            try {
                setLoading(true);

                // Dynamically import PDF.js library
                const pdfjsLib = await import('pdfjs-dist/build/pdf');
                const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.entry');

                // Set the PDF.js worker
                pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

                // Load the PDF document
                const loadingTask = pdfjsLib.getDocument(pdfUrl);
                const pdfDoc = await loadingTask.promise;

                setPdf(pdfDoc);
                setTotalPages(pdfDoc.numPages);

                // Pre-load first page
                await loadPage(pdfDoc, 1);
                if (pdfDoc.numPages > 1) {
                    await loadPage(pdfDoc, 2);
                }

                setLoading(false);
            } catch (error) {
                console.error('Error loading PDF:', error);
                setLoading(false);
            }
        };

        loadPdf();
    }, [pdfUrl]);

    // Function to load and render a specific page
    const loadPage = async (pdfDoc, pageNumber) => {
        try {
            const page = await pdfDoc.getPage(pageNumber);
            const viewport = page.getViewport({ scale: 1.5 });

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            await page.render({
                canvasContext: context,
                viewport: viewport
            }).promise;

            const imageUrl = canvas.toDataURL('image/png');

            setPageImages(prev => {
                const newImages = [...prev];
                newImages[pageNumber - 1] = imageUrl;
                return newImages;
            });

            return imageUrl;
        } catch (error) {
            console.error(`Error rendering page ${pageNumber}:`, error);
            return null;
        }
    };

    // Load adjacent pages when current page changes
    useEffect(() => {
        const loadAdjacentPages = async () => {
            if (!pdf) return;

            // Always load current page
            if (!pageImages[currentPage - 1]) {
                await loadPage(pdf, currentPage);
            }

            // In desktop view with spread, load next page
            if (!isMobileView) {
                const nextPage = currentPage + 1;
                if (currentPage !== 1 && nextPage <= totalPages && !pageImages[nextPage - 1]) {
                    await loadPage(pdf, nextPage);
                }
            }

            // Preload next and previous for smoother navigation
            const preloadNext = isMobileView ? currentPage + 1 : currentPage + 2;
            const preloadPrev = isMobileView ? currentPage - 1 : currentPage - 2;

            if (preloadNext <= totalPages && !pageImages[preloadNext - 1]) {
                await loadPage(pdf, preloadNext);
            }

            if (preloadPrev >= 1 && !pageImages[preloadPrev - 1]) {
                await loadPage(pdf, preloadPrev);
            }
        };

        loadAdjacentPages();
    }, [pdf, currentPage, totalPages, pageImages, isMobileView]);

    // Navigation functions - different behavior for mobile vs desktop
    // Go to first page
    const goToFirstPage = () => {
        if (currentPage !== 1 && !flipping) {
            setDirection('left');
            setFlipping(true);

            setTimeout(() => {
                setCurrentPage(1);

                setTimeout(() => {
                    setFlipping(false);
                    setDirection(null);
                }, 100);
            }, 100);
        }
    };

    // Go to last page
    const goToLastPage = () => {
        if (currentPage !== totalPages && !flipping) {
            setDirection('right');
            setFlipping(true);

            setTimeout(() => {
                setCurrentPage(totalPages);

                setTimeout(() => {
                    setFlipping(false);
                    setDirection(null);
                }, 100);
            }, 100);
        }
    };

    // Go to next page
    const goToNextPage = () => {
        if (currentPage < totalPages && !flipping) {
            setDirection('right');
            setFlipping(true);

            setTimeout(() => {
                if (isMobileView) {
                    // In mobile view: simply advance by 1 page
                    setCurrentPage(prev => Math.min(prev + 1, totalPages));
                } else {
                    // In desktop view: handle double page spreads
                    if (currentPage === 1) {
                        // From cover to first spread
                        setCurrentPage(2);
                    } else {
                        // Advance by 2 pages in spreads
                        setCurrentPage(prev => Math.min(prev + 2, totalPages));
                    }
                }

                setTimeout(() => {
                    setFlipping(false);
                    setDirection(null);
                }, 100);
            }, 100);
        }
    };

    // Go to previous page
    const goToPrevPage = () => {
        if (currentPage > 1 && !flipping) {
            setDirection('left');
            setFlipping(true);

            setTimeout(() => {
                if (isMobileView) {
                    // In mobile view: simply go back by 1 page
                    setCurrentPage(prev => Math.max(prev - 1, 1));
                } else {
                    // In desktop view: handle double page spreads
                    if (currentPage === 2 || currentPage === 3) {
                        // Go back to cover
                        setCurrentPage(1);
                    } else {
                        // Go back by 2 pages in spreads
                        setCurrentPage(prev => Math.max(prev - 2, 2));
                    }
                }

                setTimeout(() => {
                    setFlipping(false);
                    setDirection(null);
                }, 100);
            }, 100);
        }
    };

    // Handle zoom
    const zoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2));
    const zoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));
    const resetZoom = () => setZoom(1);

    // Render book pages
    const renderPages = () => {
        if (loading || !pageImages.length) {
            return (
                <div className="magazine-loading">
                    <Loader className="animate-spin" size={36} />
                    <p>Chargement du magazine...</p>
                </div>
            );
        }

        // In mobile view, always show just the current page
        if (isMobileView) {
            const currentPageImage = pageImages[currentPage - 1];

            return (
                <div className={`magazine-book-container mobile-view ${flipping ? 'flipping' : ''} ${direction ? `flip-${direction}` : ''}`} style={{ transform: `scale(${zoom})` }}>
                    <div className="magazine-page page-single">
                        {currentPageImage ? (
                            <img src={currentPageImage} alt={`Page ${currentPage}`} className="page-image" />
                        ) : (
                            <div className="page-loading">
                                <Loader className="animate-spin" size={24} />
                            </div>
                        )}
                        <div className="page-number">{currentPage}</div>
                    </div>
                </div>
            );
        }

        // Desktop view - handle spreads (showing two pages side by side)
        // First page (cover) is solo, then pages are shown in pairs (spreads).
        // The last page is also solo when it falls outside a spread pair.
        const isCoverView = currentPage === 1;
        const isLastSoloPage = !isCoverView && currentPage === totalPages;
        const isSinglePageView = isCoverView || isLastSoloPage;

        // For left page
        const leftPageIndex = currentPage - 1;
        const leftPageImage = pageImages[leftPageIndex];

        // For right page - only shown in a true spread (not cover, not last solo page)
        const showRightPage = !isSinglePageView;
        const rightPageIndex = currentPage;
        const rightPageImage = showRightPage ? pageImages[rightPageIndex] : null;

        return (
            <div className={`magazine-book-container ${flipping ? 'flipping' : ''} ${direction ? `flip-${direction}` : ''} ${isSinglePageView ? 'cover-view' : 'spread-view'}`} style={{ transform: `scale(${zoom})` }}>
                {/* Left / Single Page */}
                <div className={`magazine-page ${isSinglePageView ? 'page-cover' : 'page-left'} ${!leftPageImage ? 'loading-page' : ''}`}>
                    {leftPageImage ? (
                        <img src={leftPageImage} alt={`Page ${leftPageIndex + 1}`} className="page-image" />
                    ) : (
                        <div className="page-loading">
                            <Loader className="animate-spin" size={24} />
                        </div>
                    )}
                    <div className="page-number">{leftPageIndex + 1}</div>
                </div>

                {/* Right Page - only shown in spread view */}
                {showRightPage && (
                    <div className={`magazine-page page-right ${!rightPageImage ? 'loading-page' : ''}`}>
                        {rightPageImage ? (
                            <img src={rightPageImage} alt={`Page ${rightPageIndex + 1}`} className="page-image" />
                        ) : (
                            <div className="page-loading">
                                <Loader className="animate-spin" size={24} />
                            </div>
                        )}
                        <div className="page-number">{rightPageIndex + 1}</div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="magazine-viewer-container" ref={containerRef}>
            <div className={`magazine-controls ${isMobileView ? 'mobile-controls' : ''}`}>
                <div className="page-navigation">
                    <button
                        className="btn btn-light rounded-circle nav-btn"
                        onClick={goToFirstPage}
                        disabled={currentPage === 1 || flipping}
                        title="Première page"
                        aria-label="Première page"
                    >
                        <ArrowLeftToLine size={18} />
                        {/* <ChevronLeft size={18} style={{ marginLeft: '-8px' }} /> */}
                    </button>

                    <button
                        className="btn btn-light rounded-circle nav-btn"
                        onClick={goToPrevPage}
                        disabled={currentPage === 1 || flipping}
                        title="Page précédente"
                        aria-label="Page précédente"
                    >
                        <ChevronLeft size={isMobileView ? 20 : 24} />
                    </button>

                    <span className="page-info">
                        {isMobileView ? (
                            `${currentPage}/${totalPages}`
                        ) : currentPage === 1 ? (
                            `${currentPage} / ${totalPages}`
                        ) : (
                            `${currentPage}-${Math.min(currentPage + 1, totalPages)} / ${totalPages}`
                        )}
                    </span>

                    <button
                        className="btn btn-light rounded-circle nav-btn"
                        onClick={goToNextPage}
                        disabled={currentPage >= totalPages || flipping}
                        title="Page suivante"
                        aria-label="Page suivante"
                    >
                        <ChevronRight size={isMobileView ? 20 : 24} />
                    </button>
                    <button
                        className="btn btn-light rounded-circle nav-btn"
                        onClick={goToLastPage}
                        disabled={(currentPage >= totalPages) || flipping}
                        title="Dernière page"
                        aria-label="Dernière page"
                    >
                        <ArrowRightToLine size={18} />
                        {/* <ChevronRight size={18} style={{ marginLeft: '-8px' }} /> */}
                    </button>
                </div>

                {/* Toggle between mobile/desktop view button for tablets */}
                {window.innerWidth >= 600 && window.innerWidth < 992 && (
                    <button
                        className="btn btn-light rounded-circle view-toggle"
                        onClick={() => setIsMobileView(!isMobileView)}
                        title={isMobileView ? "Vue double page" : "Vue simple page"}
                        aria-label={isMobileView ? "Vue double page" : "Vue simple page"}
                    >
                        <Book size={20} />
                    </button>
                )}

                <div className="zoom-controls">
                    <button
                        className="btn btn-light rounded-circle zoom-btn"
                        onClick={zoomOut}
                        title="Zoom arrière"
                        aria-label="Zoom arrière"
                    >
                        <ZoomOut size={isMobileView ? 18 : 20} />
                    </button>

                    <button
                        className="btn btn-light rounded-circle zoom-btn"
                        onClick={resetZoom}
                        title="Réinitialiser le zoom"
                        aria-label="Réinitialiser le zoom"
                    >
                        <RotateCcw size={isMobileView ? 18 : 20} />
                    </button>

                    <button
                        className="btn btn-light rounded-circle zoom-btn"
                        onClick={zoomIn}
                        title="Zoom avant"
                        aria-label="Zoom avant"
                    >
                        <ZoomIn size={isMobileView ? 18 : 20} />
                    </button>
                </div>
            </div>

            <div className="magazine-content">
                {renderPages()}
            </div>
        </div>
    );
};

export default MagazineViewer;